import { FEATURE_FLAGS, type FeatureFlagKey } from '@/feature-flags/consts/flags.const';
import { parseAttachmentFlag, parseAttachmentStringList } from '@/feature-flags/utils/attachment-parser';

export interface FeatureDefinition<TValue> {
  flagKey: FeatureFlagKey;
  attachmentKey: string | null;
  fallback: TValue;
  parse: (attachment: string | null | undefined) => TValue;
}

function flagFeature(flagKey: FeatureFlagKey, attachmentKey: string): FeatureDefinition<boolean> {
  return {
    flagKey,
    attachmentKey,
    fallback: false,
    parse: (attachment) => parseAttachmentFlag(attachment, attachmentKey),
  };
}

function payloadFeature<TValue>(
  flagKey: FeatureFlagKey,
  fallback: TValue,
  parse: (attachment: string | null | undefined) => TValue,
): FeatureDefinition<TValue> {
  return { flagKey, attachmentKey: null, fallback, parse };
}

const {
  OPERATIONAL_MEDPREV_WEB_APP_LEVEL_TWO: ACCOUNT_FLAG,
  OPERATIONAL_MEDPREV_WEB_APP_LEVEL_THREE: BOOKING_FLAG,
  OPERATIONAL_MEDPREV_WEB_APP_SWITCH_FLEX_TO_WHATSAPP: WHATSAPP_FLAG,
} = FEATURE_FLAGS;

export const FEATURES = {
  newLogin: flagFeature(ACCOUNT_FLAG, 'newLogin'),
  newRegister: flagFeature(ACCOUNT_FLAG, 'newRegister'),
  newProfile: flagFeature(ACCOUNT_FLAG, 'newProfile'),
  newFavorites: flagFeature(ACCOUNT_FLAG, 'newFavorites'),
  newDependents: flagFeature(ACCOUNT_FLAG, 'newDependents'),
  newAppointments: flagFeature(ACCOUNT_FLAG, 'newAppointments'),
  newSchedule: flagFeature(BOOKING_FLAG, 'newSchedule'),
  newCart: flagFeature(BOOKING_FLAG, 'newCart'),
  switchFlexToWhatsappCities: payloadFeature<string[]>(WHATSAPP_FLAG, [], parseAttachmentStringList),
} satisfies Record<string, FeatureDefinition<unknown>>;

export type FeatureName = keyof typeof FEATURES;

export type FeatureValue<TName extends FeatureName>
  = typeof FEATURES[TName] extends FeatureDefinition<infer TValue> ? TValue : never;

export type BooleanFeatureName = {
  [TName in FeatureName]: FeatureValue<TName> extends boolean ? TName : never;
}[FeatureName];

export const FEATURE_NAMES = Object.keys(FEATURES) as FeatureName[];

export const EVALUATED_FLAG_KEYS: FeatureFlagKey[] = Array.from(
  new Set(FEATURE_NAMES.map((name) => FEATURES[name].flagKey)),
);

export function getFeatureDefinition<TName extends FeatureName>(
  name: TName,
): FeatureDefinition<FeatureValue<TName>> {
  return FEATURES[name] as FeatureDefinition<FeatureValue<TName>>;
}

export function getFeatureNamesByFlagKey(flagKey: FeatureFlagKey): FeatureName[] {
  return FEATURE_NAMES.filter((name) => FEATURES[name].flagKey === flagKey);
}
